import { type HeaderProps, type TDrawerItem } from './types';

import { useTranslation } from 'next-i18next';
import { DefaultHeaderVariant } from './default-header-variant';
import { HamburgerHeaderVariant } from './hamburger-header-variant';
import { HEADER_VARIANT_TYPE } from './header.constants';
import { useMemo } from 'react';
import { HeaderProvider, type HeaderContextValue } from './context/header-context';
import { HeaderStateProvider } from './context/header-state-context';

export const HeaderWrapper = ({
  ref: forwardedRef,
  megaNavLinks,
  brand,
  isFluid = true,
  mainNavLinks,
  onSignOut,
  onSignInAttempt,
  suppressLogo = false,
  shouldSuppressHonorsAccountLinks,
  shouldSuppressFindStay,
  shouldSuppressJoinAndSignIn,
  shouldSuppressB2BLink,
  shouldSuppressGoHiltonLink,
  shouldSuppressHGVMaxLink,
  shouldSuppressFriendsAndFamilyLink,
  shouldOpenAccountLinksInNewWindow,
  shouldOpenJoinInNewWindow,
  shouldOpenJoinInPage,
  user,
  userLinks,
  loginOptions,
  children,
  languageSelectorOptions,
  showNewHeader = true,
  heading,
  wrapperId = '__next',
  surfaceType = 'base',
  headerVariantType,
  globalNavLinks,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
  isTranslucent,
  isReimagineThemeExperience,
  navHeaderLinksLogoSurfaceType,
  navHeaderLinksLogoColors,
  navHeaderLinksLogoVariant,
  globalNavLogoSurfaceType,
  globalNavLogoColors,
  globalNavLogoVariant,
  analytics,
  ...rest
}: HeaderProps & {
  globalNavLinks?: TDrawerItem[];
}) => {
  const [t] = useTranslation(['osc-header', 'osc-login']);

  const hasGlobalNavLinks = !!globalNavLinks?.length;
  const hasMegaNavLinks = !!megaNavLinks?.length;

  const isHamburgerVariant = headerVariantType === HEADER_VARIANT_TYPE.hamburgerNav;
  const isHamburgerNavMinimalVariant =
    headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal;

  const effectiveHeaderVariantType = useMemo(() => {
    if (isHamburgerVariant && hasGlobalNavLinks && !hasMegaNavLinks) {
      return HEADER_VARIANT_TYPE.hamburgerNavMinimal;
    } else if (
      (isHamburgerVariant || isHamburgerNavMinimalVariant) &&
      !hasGlobalNavLinks &&
      !hasMegaNavLinks
    ) {
      return HEADER_VARIANT_TYPE.default;
    }
    return headerVariantType;
  }, [
    headerVariantType,
    hasMegaNavLinks,
    hasGlobalNavLinks,
    isHamburgerVariant,
    isHamburgerNavMinimalVariant,
  ]);

  const showDefaultNavOrMinimalNavVariant =
    effectiveHeaderVariantType === HEADER_VARIANT_TYPE.default ||
    effectiveHeaderVariantType === HEADER_VARIANT_TYPE.minimal ||
    effectiveHeaderVariantType === undefined;

  const showHamburgerNavVariant =
    effectiveHeaderVariantType === HEADER_VARIANT_TYPE.hamburgerNav ||
    effectiveHeaderVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal;

  // Create context value for child components
  const headerContextValue: HeaderContextValue = useMemo(
    () => ({
      brand,
      user: {
        user,
        onSignInAttempt,
        onSignOut,
        loginOptions,
        analytics,
      },
      visual: {
        surfaceType: surfaceType || 'base',
        isTranslucent,
        isFluid,
        suppressLogo,
        heading,
        isReimagineThemeExperience,
      },
      features: {
        shouldSuppressFindStay: !!shouldSuppressFindStay,
        shouldSuppressJoinAndSignIn: !!shouldSuppressJoinAndSignIn,
        shouldOpenAccountLinksInNewWindow: !!shouldOpenAccountLinksInNewWindow,
        shouldOpenJoinInNewWindow: !!shouldOpenJoinInNewWindow,
        shouldOpenJoinInPage: !!shouldOpenJoinInPage,
        shouldSuppressHonorsAccountLinks: !!shouldSuppressHonorsAccountLinks,
        shouldSuppressB2BLink,
        shouldSuppressGoHiltonLink,
        shouldSuppressHGVMaxLink,
        shouldSuppressFriendsAndFamilyLink,
      },
      navigation: {
        menuItems: megaNavLinks,
        globalNavLinks,
        mainNavLinks,
        onGlobalNavigationClick,
        isLegacyGlobalNavigationClickAnalyticsEnabled,
        languageSelectorOptions,
        headerVariantType: effectiveHeaderVariantType,
        globalNavCustomClassName: rest.globalNavCustomClassName,
        bottomNavLinkAreaClassName: rest.bottomNavLinkAreaClassName,
        navHeaderLinksLogoSurfaceType,
        navHeaderLinksLogoColors,
        navHeaderLinksLogoVariant,
        globalNavLogoSurfaceType,
        globalNavLogoColors,
        globalNavLogoVariant,
      },
      accessibility: {
        wrapperId,
      },
    }),
    [
      brand,
      user,
      onSignInAttempt,
      onSignOut,
      loginOptions,
      analytics,
      surfaceType,
      isTranslucent,
      isFluid,
      suppressLogo,
      heading,
      isReimagineThemeExperience,
      shouldSuppressFindStay,
      shouldSuppressJoinAndSignIn,
      shouldOpenAccountLinksInNewWindow,
      shouldOpenJoinInNewWindow,
      shouldOpenJoinInPage,
      shouldSuppressHonorsAccountLinks,
      shouldSuppressB2BLink,
      shouldSuppressGoHiltonLink,
      shouldSuppressHGVMaxLink,
      shouldSuppressFriendsAndFamilyLink,
      megaNavLinks,
      globalNavLinks,
      mainNavLinks,
      onGlobalNavigationClick,
      isLegacyGlobalNavigationClickAnalyticsEnabled,
      languageSelectorOptions,
      effectiveHeaderVariantType,
      rest.globalNavCustomClassName,
      rest.bottomNavLinkAreaClassName,
      navHeaderLinksLogoSurfaceType,
      navHeaderLinksLogoColors,
      navHeaderLinksLogoVariant,
      globalNavLogoSurfaceType,
      globalNavLogoColors,
      globalNavLogoVariant,
      wrapperId,
    ]
  );

  return (
    <>
      <a href="#skipped" className="btn--lg btn--flat sr-only focus:not-sr-only">
        {t('skipToContent')}
      </a>
      <HeaderProvider value={headerContextValue}>
        <HeaderStateProvider>
          <header
            className="header-bottom-border border-solid"
            data-testid="shared-header"
            ref={forwardedRef}
            {...rest}
          >
            {showNewHeader ? (
              showDefaultNavOrMinimalNavVariant ? (
                <DefaultHeaderVariant surfaceClassName={rest.className} />
              ) : showHamburgerNavVariant ? (
                <HamburgerHeaderVariant surfaceClassName={rest.className} />
              ) : null
            ) : null}
          </header>
          {children}
        </HeaderStateProvider>
      </HeaderProvider>
      <div id="skipped" tabIndex={-1} />
    </>
  );
};
